import type { FormStep, GuideChatProduct, OpenerTrigger, PageKind } from './types';

export type DeviceClass = 'desktop' | 'mobile';

export const INACTIVITY_MS = 20_000;
export const INACTIVITY_EVENTS = ['scroll', 'touchstart', 'touchmove', 'pointerdown', 'keydown', 'input'] as const;
export const FORM_LEAVING_TRIGGERS: readonly OpenerTrigger[] = ['exit_intent', 'focus_loss', 'inactivity'];

const ARTICLE_PREFIX = '/guide/assurance-pret-immobilier';
// The public /assurance-pret-immobilier redirects to this WordPress pillar page of the guide, which acts as the product landing.
const LANDING_PAGE = '/guide/assurance-pret-immobilier';
const LANDING_PREFIXES = ['/assurance-pret-immobilier', '/simulation-assurance-pret-immobilier'];
const FORM_PREFIXES = [
  '/devis-assurance-pret-immobilier',
  '/parcours-assurance-pret-immobilier',
  '/coordonnees-assurance-pret-immobilier',
  '/adhesion-assurance-pret-immobilier',
  '/resultats-assurance-pret-immobilier',
  '/mon-compte-assurance-pret-immobilier',
];

// Each pillar page is its product's landing and the rest are articles; only the loan wizard is readable, so no other form triggers.
const PRODUCT_GUIDES: Record<Exclude<GuideChatProduct, 'loan'>, string[]> = {
  health: ['/guide/mutuelle-sante'],
  travel: ['/guide/assurance-voyage'],
  provident: ['/guide/prevoyance', '/guide/assurance-dependance', '/guide/assurance-deces', '/guide/assurance-obseques'],
};

export interface GuideChatScope {
  kind: PageKind;
  product: GuideChatProduct;
}

export function guideChatScopeForPath(pathname: string): GuideChatScope | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === LANDING_PAGE) return { kind: 'landing', product: 'loan' };
  if (path.startsWith(`${ARTICLE_PREFIX}/`)) return { kind: 'article', product: 'loan' };
  if (LANDING_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return { kind: 'landing', product: 'loan' };
  if (FORM_PREFIXES.some((p) => path.startsWith(p))) return { kind: 'form', product: 'loan' };
  for (const [product, cocoons] of Object.entries(PRODUCT_GUIDES) as [Exclude<GuideChatProduct, 'loan'>, string[]][]) {
    if (cocoons.includes(path)) return { kind: 'landing', product };
    if (cocoons.some((c) => path.startsWith(`${c}/`))) return { kind: 'article', product };
  }
  return null;
}

export function guideChatKindForPath(pathname: string): PageKind | null {
  return guideChatScopeForPath(pathname)?.kind ?? null;
}

export function guideChatProductForPath(pathname: string): GuideChatProduct | null {
  return guideChatScopeForPath(pathname)?.product ?? null;
}

// One proactive opener per page view, or per wizard step on the form.
export function openerKey(pathname: string, step: FormStep | null | undefined): string {
  return step ? `${pathname}#${step}` : pathname;
}

// Phones have no exit intent and back or tab switches misfire there, so inactivity is their only leaving signal.
export function leaveTriggersFor(kind: PageKind, device: DeviceClass): OpenerTrigger[] {
  if (device === 'mobile') return ['inactivity'];
  return kind === 'form' ? ['exit_intent', 'focus_loss'] : ['exit_intent'];
}

export function isFormLeavingTrigger(trigger: OpenerTrigger): boolean {
  return FORM_LEAVING_TRIGGERS.includes(trigger);
}

export function watchInactivity(target: EventTarget, onIdle: () => void, ms: number = INACTIVITY_MS): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const arm = () => {
    clear();
    timer = setTimeout(() => {
      timer = null;
      onIdle();
    }, ms);
  };
  INACTIVITY_EVENTS.forEach((event) => target.addEventListener(event, arm, { passive: true }));
  arm();
  return () => {
    clear();
    INACTIVITY_EVENTS.forEach((event) => target.removeEventListener(event, arm));
  };
}
